/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

import Card from "@mui/material/Card";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import breakpoints from "assets/theme/base/breakpoints";

// Data
// import authorsTableData from "layouts/tables/data/authorsTableData";
import projectsTableData from "layouts/tables/data/projectsTableData";
// import tutorTableData from "Views/people/tutor/data/tutorTableData";
// import TestUserTableData from "Views/TestUser/data/TestUserTableData"
// import StudentLeaderBoardTableData from "Views/StudentLeaderBoard/data/StudentLeaderBoardTableData"

import TutorLeaderBoardTableData from "Views/TutorLeaderBoard/data/TutorLeaderBoardTableData";
import { useState, useEffect } from "react";
import ArgonButton from "components/ArgonButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import ArgonInput from "components/ArgonInput";
import Dropdown from "components/CustomDropdown/Dropdown";
import { toast } from "react-toastify";
import api from "utils/api";
import { getTutorLeaderBoards, createTutorLeaderBoard } from "services/tutorLeaderBoardService";
import PaginationBar from "components/PaginationBar/PaginationBar";

function Tables() {
  const [leaderBoardData, setLeaderBoardData] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const [formData, setFormData] = useState({
    leader1: "",
    leader2: "",
    leader3: "",
    month: "",
    year: new Date().getFullYear(),
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch tutors for dropdown
  useEffect(() => {
    fetchTutors();
  }, []);

  useEffect(() => {
    fetchLeaderBoards(currentPage, rowsPerPage);
  }, [currentPage, rowsPerPage]);

  const fetchTutors = async () => {
    try {
      const approvedTutors = [];
      let page = 1;
      let hasNextPage = true;

      while (hasNextPage) {
        const response = await api.get("/tutor", {
          params: {
            approvalStatus: "APPROVED",
            page,
            limit: 100,
            sort: "fullName",
            order: "asc",
          },
        });

        if (!response.data.success) break;

        approvedTutors.push(...(response.data.data.tutors || []));
        hasNextPage = Boolean(response.data.data.pagination?.hasNextPage);
        page += 1;
      }

      setTutors(approvedTutors.filter((tutor) => tutor.active !== false));
    } catch (error) {
      console.error("Error fetching tutors:", error);
      toast.error("Failed to fetch tutors");
    }
  };

  const fetchLeaderBoards = async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      const response = await getTutorLeaderBoards({ page, limit });
      if (response.success) {
        setLeaderBoardData(response.data);
        setTotalItems(response.pagination?.total || 0);
      }
    } catch (error) {
      console.error("Error fetching leader boards:", error);
      toast.error("Failed to fetch leader boards");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      // Validation
      if (
        !formData.leader1 ||
        !formData.leader2 ||
        !formData.leader3 ||
        !formData.month ||
        !formData.year
      ) {
        toast.error("Please fill all fields");
        return;
      }

      if (
        formData.leader1 === formData.leader2 ||
        formData.leader2 === formData.leader3 ||
        formData.leader1 === formData.leader3
      ) {
        toast.error("All three leaders must be different tutors");
        return;
      }

      setLoading(true);
      const response = await createTutorLeaderBoard(formData);

      if (response.success) {
        toast.success("Leader board created successfully!");
        handleClose();
        fetchLeaderBoards(currentPage, rowsPerPage); // Refresh the list with current pagination
      }
    } catch (error) {
      console.error("Error creating leader board:", error);
      toast.error(error.message || "Failed to create leader board");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      leader1: "",
      leader2: "",
      leader3: "",
      month: "",
      year: new Date().getFullYear(),
    });
  };

  // Pagination handlers
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  const { columns, rows } = TutorLeaderBoardTableData(leaderBoardData);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3} display="flex" justifyContent="space-between" alignItems="center">
              <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                Tutor Leader Board
              </ArgonTypography>
              <ArgonButton onClick={handleOpen} size="small" variant="contained" color="primary">
                Add
              </ArgonButton>
            </ArgonBox>
            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Fetching leader boards...
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Please wait while we load the latest data.
                  </ArgonTypography>
                </ArgonBox>
              ) : leaderBoardData.length > 0 ? (
                <Table columns={columns} rows={rows} />
              ) : (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    No leader boards found
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Try adding a new leader board entry.
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonBox>

            {/* Pagination Controls */}
            {leaderBoardData.length > 0 && (
              <PaginationBar
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </Card>
        </ArgonBox>

        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          {/* Header */}
          <DialogTitle>
            <ArgonTypography variant="h5" fontWeight="bold" color="dark">
              Add Tutor Leader Board
            </ArgonTypography>
          </DialogTitle>

          {/* Body */}
          <DialogContent>
            <ArgonBox py={2}>
              <Grid container spacing={3}>
                {/* Month */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="button" fontWeight="medium" display="block" mb={1}>
                    Month
                  </ArgonTypography>
                  <Dropdown
                    value={formData.month}
                    options={[
                      { value: "January", label: "January" },
                      { value: "February", label: "February" },
                      { value: "March", label: "March" },
                      { value: "April", label: "April" },
                      { value: "May", label: "May" },
                      { value: "June", label: "June" },
                      { value: "July", label: "July" },
                      { value: "August", label: "August" },
                      { value: "September", label: "September" },
                      { value: "October", label: "October" },
                      { value: "November", label: "November" },
                      { value: "December", label: "December" },
                    ]}
                    onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                    placeholder="Select Month"
                  />
                </Grid>

                {/* Year */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="button" fontWeight="medium" display="block" mb={1}>
                    Year
                  </ArgonTypography>
                  <ArgonInput
                    fullWidth
                    type="number"
                    name="year"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                    placeholder="Enter Year"
                  />
                </Grid>

                {/* Leader 1 */}
                <Grid item xs={12}>
                  <ArgonTypography variant="button" fontWeight="medium" display="block" mb={1}>
                    Leader 1 (1st Rank)
                  </ArgonTypography>
                  <Dropdown
                    value={formData.leader1}
                    options={
                      Array.isArray(tutors)
                        ? tutors.map((tutor) => ({ value: tutor.id, label: tutor.fullName }))
                        : []
                    }
                    onChange={(e) => setFormData({ ...formData, leader1: e.target.value })}
                    width={550}
                    placeholder="Select Tutor"
                  />
                </Grid>

                {/* Leader 2 */}
                <Grid item xs={12}>
                  <ArgonTypography variant="button" fontWeight="medium" display="block" mb={1}>
                    Leader 2 (2nd Rank)
                  </ArgonTypography>
                  <Dropdown
                    value={formData.leader2}
                    options={
                      Array.isArray(tutors)
                        ? tutors.map((tutor) => ({ value: tutor.id, label: tutor.fullName }))
                        : []
                    }
                    onChange={(e) => setFormData({ ...formData, leader2: e.target.value })}
                    width={550}
                    placeholder="Select Tutor"
                  />
                </Grid>

                {/* Leader 3 */}
                <Grid item xs={12}>
                  <ArgonTypography variant="button" fontWeight="medium" display="block" mb={1}>
                    Leader 3 (3rd Rank)
                  </ArgonTypography>
                  <Dropdown
                    value={formData.leader3}
                    options={
                      Array.isArray(tutors)
                        ? tutors.map((tutor) => ({ value: tutor.id, label: tutor.fullName }))
                        : []
                    }
                    onChange={(e) => setFormData({ ...formData, leader3: e.target.value })}
                    width={550}
                    placeholder="Select Tutor"
                  />
                </Grid>
              </Grid>

              {/* Footer Buttons */}
              <ArgonBox display="flex" justifyContent="space-between" mt={4}>
                <ArgonButton variant="outlined" color="secondary" onClick={handleClose}>
                  Cancel
                </ArgonButton>
                <ArgonButton
                  variant="gradient"
                  color="info"
                  sx={{ backgroundColor: "rgb(8, 2, 120)" }}
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save"}
                </ArgonButton>
              </ArgonBox>
            </ArgonBox>
          </DialogContent>
        </Dialog>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;

